import { scopedAdminUploadApi } from "./adminScopedUploadApi";
import { adminUploadScopeMismatch, readCurrentAdminUploadScope } from "./adminUploadAuthScope";
import {
	AimdConcurrencyController as SharedAimdConcurrencyController,
	CHUNK_SIZE,
	AmbiguousUploadTargetError,
	ChunkNetworkError,
	classifyUploadFailure,
	isTransportShapedS3Failure,
	MultipartUploadGoneError,
	MissingEtagError,
	MultipartUrlRefreshError,
	S3HttpError,
	TruncatedPartsError,
	UploadCancelledError,
	defaultRetryDelay,
	formatFileSize,
	getChunkSize,
	hasContiguousParts,
	listPartsEvidenceFromResponse,
	processWithAdaptiveConcurrency,
	processWithConcurrency,
	projectS3ChunkResponse,
	RESUME_OUTCOME,
	newUploadId,
	uploadTargetChunkUrls,
	uploadTargetOpaqueId,
	isUploadsUploadTarget,
	createBrowserConnectivityPort,
	announcesMultipartAttemptMigration,
	chunkedObjectLaneColumns,
	createUploadsMultipartUploadTarget,
	isChunkedObjectAssemblyIntegrityError,
	isUploadPartsIncompleteError,
	parsePersistedChunkUrls,
	preparePersistedUploadUrls,
	isUploadSettlingConflict,
	persistedUploadTarget,
	isUploadSupersededError,
	isUploadTerminalFailure,
	tagUploadRefusalTrigger,
	readUploadFacadeErrorCode,
	runGuardedEffect,
	withFacadeErrorCodeInMessage,
	UPLOAD_REASON_SAFE,
	UPLOAD_PROGRESS_LEASE_TOUCH_INTERVAL_MS,
	UPLOAD_SETTLING_EXPIRED_CODE,
	UPLOAD_SUPERSEDED_ERROR_CODE,
	type ChunkedObjectTargetBlock,
	type DurableLeaseToken,
	type FamilyAwareMultipartUploadTarget,
	type UploadTargetEnvelope,
	type S3ChunkTransport,
	type ChunkedUploadDriverPorts,
	type DriverChunkMeta,
	type MultipartTargetValidationFailure,
	type MultipartSessionShape,
	type NewSessionInput,
	type UploadLease,
	type UploadLeaseScope
} from "@uploads";
import api from "@/api";
import type { MultipartUploadTarget } from "@/types/uploadTarget";
import { putToS3WithStallTimeout } from "@/helpers/s3";
import TransientAuthTokenError, { isTransientAuthTokenError } from "@/types/errors/TransientAuthTokenError";
import {
	isIdbInternalError,
	isIdbQuotaError,
	getChunkBuffer,
	type FileUploadChunkMeta,
	type FileUploadSession,
	type FileCategory
} from "@/db/fileUploadDb";
import { hasNoServerUploadTarget } from "./pendingUploadTarget";
import { createAdminSessionStore } from "@/services/uploads/adminSessionStore";
import { createAdminChunkSourceFactory, FileUnreadableError, isFileReadError } from "@/services/uploads/adminChunkSource";
import { createAdminPersistedTargetInitiator } from "@/services/uploads/adminUploadTargetInitiator";
import { createAdminUploadLease, registerAdminFreshSessionClaim } from "@/services/uploads/adminUploadLease";
import { createAdminOwnedOrUnownedGate, recoverAdminTerminalSession } from "@/services/uploads/adminPersistedSessionReconciler";
import { createPersistedAdminTerminalRecoveryPolicy } from "@/services/uploads/adminTerminalRecoveryPolicy";
import { adminUploadTelemetry } from "@/services/uploads/adminUploadTelemetry";
import { uploadFailureLogDetails } from "@/services/uploads/uploadLogRedaction";
import { readUploadRegistryForCompletionRefusal } from "@/services/uploads/adminUploadRegistryProbe";

export { adminUploadTelemetry } from "@/services/uploads/adminUploadTelemetry";

/**
 * Is this object-store rejection a DETERMINISTIC refusal of these exact bytes — the only thing this
 * driver may park an upload for?
 *
 * The S3 vocabulary half lives in `@uploads` (`isTransportShapedS3Failure`, beside the `S3HttpError`
 * that carries the code) so the foreground lane in `FileUploadDialog.vue` reads the same rule; a
 * second copy here is how the two lanes would end up disagreeing about the same error.
 *
 * Silent-failure rule: BOTH exclusions log. This whole ticket exists because "why is this upload
 * looping?" could not be answered from the logs, and an exclusion that silently declines to park is
 * exactly the condition the next such investigation will need named.
 */
function isDeterministicObjectStoreRejection(error: S3HttpError): boolean {
	if (isTransportShapedS3Failure(error)) {
		console.warn("[Upload] The storage rejection looks like a transport problem, not a verdict; keeping the upload retryable", {
			httpStatus: error.status,
			s3Code: error.code ?? "absent",
			...uploadFailureLogDetails(error)
		});
		return false;
	}
	const terminal = classifyUploadFailure(error).kind === "terminal";
	if (!terminal) {
		console.warn("[Upload] The storage rejection carries no permanence evidence; keeping the upload retryable", {
			httpStatus: error.status,
			s3Code: error.code ?? "absent",
			...uploadFailureLogDetails(error)
		});
	}
	return terminal;
}

const UPLOAD_ATTEMPT_UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export class AimdConcurrencyController extends SharedAimdConcurrencyController {
	constructor(maxConcurrency: number, context?: { sessionId?: string; conversationId?: string }) {
		super(maxConcurrency, adminUploadTelemetry, context);
	}
}

// Exported so an identity-bearing discard can reach the SAME store instance the driver ports use.
export const adminSessionStore = createAdminSessionStore();
export const adminUploadLease = createAdminUploadLease();
type AdminLeaseScope = UploadLeaseScope<FileUploadSession, unknown> & {
	releaseAndWait(session: FileUploadSession): Promise<void>;
};

function createAdminLeaseScope(
	purpose: Parameters<AdminDriverPorts["leaseCoordinator"]["createScope"]>[0],
	sessionId?: string,
	requestedToken?: unknown | (() => unknown),
	registerFreshClaim = true
): AdminLeaseScope {
	const expectedOwnerClaimId = () => {
		const token = typeof requestedToken === "function" ? requestedToken() : requestedToken;
		return typeof token === "string" && token.length > 0 ? token : undefined;
	};
	const scopedUploadLease = createAdminUploadLease(undefined, expectedOwnerClaimId, purpose);
	const scopedOwnershipGate = createAdminOwnedOrUnownedGate(scopedUploadLease, adminSessionStore);
	const startingScope = readCurrentAdminUploadScope();
	const authCurrent = (session: FileUploadSession): boolean => {
		// Preparing sources remains allowed after a switch: these may be the only copy of the bytes.
		if (purpose === "pre-persist") return true;
		const scope = readCurrentAdminUploadScope();
		if (!scope) return !session.batchOrganizationId && !session.batchUserId;
		const current = scope.generation === startingScope?.generation && !adminUploadScopeMismatch(session, scope);
		if (!current) console.warn("[Upload] Upload attempt yielded after auth identity changed", { sessionId: session.id });
		return current;
	};
	let capturedToken: DurableLeaseToken | undefined;
	let released = false;
	const lease: UploadLease<FileUploadSession> = {
		...scopedUploadLease,
		claim: async (session) => {
			if (!authCurrent(session)) return false;
			const claimed = await scopedUploadLease.claim(session);
			if (claimed) {
				// claim() has just synchronously stored this epoch. Capture it before another
				// await can let a same-tab retry replace the durable token.
				capturedToken = scopedUploadLease.getDurableToken(session.id);
				released = false;
				if (!authCurrent(session)) {
					await releaseAndWait(session);
					return false;
				}
			}
			return claimed;
		},
		owns: async (session) => authCurrent(session) && (await scopedOwnershipGate.owns(session)) && authCurrent(session),
		commitIfOwned: (session, effect) => {
			// The shared lease contract keeps this hook optional for adapters that only
			// expose an owns() check. Admin's concrete gate always supplies the durable
			// writer-fenced implementation; fail closed if that invariant ever changes
			// instead of allowing a post-PUT mutation to bypass the epoch fence.
			if (!scopedOwnershipGate.commitIfOwned) throw new Error("Admin upload ownership gate is missing its durable commit fence");
			if (!authCurrent(session)) return Promise.resolve({ kind: "skipped-not-owned" } as const);
			return scopedOwnershipGate.commitIfOwned!(session, async () => {
				if (!authCurrent(session)) throw new TransientAuthTokenError("Upload identity changed before checkpoint.");
				return effect();
			});
		}
	};
	const unregisterFreshClaim =
		registerFreshClaim && sessionId ? registerAdminFreshSessionClaim(sessionId, (session) => lease.claim(session)) : () => undefined;
	const livenessTimers = new Set<ReturnType<typeof setInterval>>();
	let livenessTouchInFlight: Promise<void> | undefined;
	const stopLiveness = async (timer?: ReturnType<typeof setInterval>): Promise<void> => {
		const timersToStop = timer === undefined ? [...livenessTimers] : [timer];
		for (const timerToStop of timersToStop) {
			clearInterval(timerToStop);
			livenessTimers.delete(timerToStop);
		}
		await livenessTouchInFlight;
	};
	const runWithLiveness = async <T>(session: FileUploadSession, work: () => Promise<T>): Promise<T> => {
		if (purpose !== "pre-persist") return work();
		const livenessTimer = setInterval(() => {
			if (released || !livenessTimers.has(livenessTimer) || livenessTouchInFlight) return;
			const touch = scopedUploadLease.touch(session).catch((error) => {
				// The next ownership fence remains authoritative. A heartbeat failure must not
				// replace the source-read error or make this scope reacquire an expired writer.
				console.warn("[Upload] Scoped upload liveness renewal failed; guarded work will verify ownership", {
					sessionId: session.id,
					...uploadFailureLogDetails(error)
				});
			});
			const trackedTouch = touch.finally(() => {
				if (livenessTouchInFlight === trackedTouch) livenessTouchInFlight = undefined;
			});
			livenessTouchInFlight = trackedTouch;
		}, UPLOAD_PROGRESS_LEASE_TOUCH_INTERVAL_MS);
		livenessTimers.add(livenessTimer);
		try {
			return await work();
		} finally {
			await stopLiveness(livenessTimer);
		}
	};
	let releasePromise: Promise<void> | undefined;
	const releaseAndWait = (session: FileUploadSession): Promise<void> => {
		unregisterFreshClaim();
		if (released) return releasePromise ?? Promise.resolve();
		released = true;
		return (releasePromise = (async () => {
			await stopLiveness();
			try {
				if (capturedToken && !authCurrent(session) && session.status === "uploading") {
					// Auth revokes network/terminal work, not the safe handoff of this exact writer's retained bytes.
					// The ordinary retry path uses the auth gate and cannot demote after a switch.
					if (!scopedOwnershipGate.commitIfOwned) throw new Error("Admin upload ownership gate is missing its durable commit fence");
					await runGuardedEffect(scopedOwnershipGate, session, () =>
						adminSessionStore.releasePending(session, "Upload paused until its original account is active.", {
							retryAt: null,
							retryAttempt: session.retryAttempt ?? 0
						})
					);
				}
			} catch (error) {
				console.warn("[Upload] Could not hand off an auth-invalidated attempt; ordinary stale-owner recovery remains armed", {
					sessionId: session.id,
					...uploadFailureLogDetails(error)
				});
			} finally {
				await scopedUploadLease.releaseClaim(session, capturedToken);
			}
		})());
	};
	return {
		token: scopedUploadLease.ownerClaimId,
		lease,
		activate: () => undefined,
		runWithLiveness,
		release: (session) => void releaseAndWait(session),
		releaseAndWait
	};
}

interface AdminResumeScopeRecord {
	readonly token: string;
	readonly predecessor?: AdminResumeScopeRecord;
	claim?: Promise<boolean>;
	releaseForHandoff(session: FileUploadSession): Promise<void>;
}

const latestAdminResumeScopes = new Map<string, AdminResumeScopeRecord>();

/**
 * Thrown when a chunk PUT to S3 fails with `<Code>NoSuchUpload</Code>`, meaning the
 * multipart upload no longer exists on S3 (aborted, expired, or already completed).
 * Retrying the PUT can never succeed, so callers must stop the spin-retry loop
 * immediately rather than burning through MAX_CHUNK_RETRIES against a dead MPU.
 */
export { AmbiguousUploadTargetError, MultipartUploadGoneError, UploadCancelledError };

/**
 * Thrown when the server reports a multipart upload is genuinely lost: the MPU is gone
 * AND no assembled object exists (HTTP 409 `{ success: false, code: "UPLOAD_LOST" }`).
 * Retrying THIS upload ID cannot succeed, so callers stop retrying against it — but under R-1
 * ("there is no destroy", owner directive 2026-08-26) they must never answer it by deleting the
 * locally retained bytes. The recovery is a fresh multipart target minted from those same buffers
 * (`recoverLostPersistedSession` → `resetForReinitialization`); the terminal "please re-upload"
 * verdict belongs only to a session whose buffers are genuinely missing.
 * It must never be treated as success.
 */
export class UploadLostError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "UploadLostError";
	}
}

export class UploadDeferredError extends Error {
	/**
	 * LSP-19422: this reason reaches the Datadog log line verbatim, because it is what distinguishes
	 * a deliberate deferral with a kept-pending session from a terminal failure. Callers must pass an
	 * engine-authored sentence — correlation ids and a bracketed facade code are fine, a reflected
	 * response body is not, since an S3 error body carries the object key and bucket name. The marker
	 * is a symbol so a parsed server body can never claim it; see UPLOAD_REASON_SAFE.
	 */
	readonly [UPLOAD_REASON_SAFE] = true;

	cause?: unknown;

	constructor(message: string, options?: { cause?: unknown }) {
		super(message);
		this.name = "UploadDeferredError";
		this.cause = options?.cause;
	}
}

/**
 * LSP-19325: the server has TWICE said this upload does not exist — once refusing the completion,
 * and once on the ungated projection read that exists to second-guess that refusal. The upload
 * target this session holds can never be completed, and no retry of it can change that.
 *
 * Why this needs its own verdict rather than R-2's `UPLOAD_NOT_FOUND` pause. A bare
 * `UPLOAD_NOT_FOUND` is deliberately conflated (absent id / wrong tenant / no grant — see the R-2
 * header in `apps/contact-api/src/controllers/uploads.ts`), so `classifyFailure` pauses on it
 * forever rather than parking a row a restored credential could still deliver. That is right for one
 * refusal. It is a different fact once `GET /contact/uploads/:id` has ALSO answered not-found. That
 * read has no LIFECYCLE gate (it answers for sealed/ready/cancelled rows that `/parts` 409s on), but
 * it is still authorization-gated: it refuses with the same conflated code when the registry row is
 * absent/soft-deleted AND when the caller holds no grant to the owning project (`findAuthorizedSession`
 * → `assertAuthorizedTarget`, anti-enumeration). So two not-founds prove "the server does not
 * currently recognize this upload for this caller" — not "it can never be delivered" (Fable review).
 * That is still the right moment to stop advertising an automatic retry: the client had been
 * re-sending the identical dead presigned URL and S3 upload id indefinitely while the tray promised
 * "We'll keep retrying automatically.", and for the aborted/expired-target case nothing will change.
 * For the revoked-then-regranted case the durable row is NOT parked for good — see the R-0 point
 * below — so the file still lands once access returns.
 *
 * `needs-attention`, NOT a discard and NOT a park:
 *  - R-1 ("there is no destroy"): every retained chunk buffer stays on disk. Nothing is deleted and
 *    the user is never told to re-upload a file the browser is still holding.
 *  - R-0 ("pacing, not parking"): `requeueNeedsAttentionFileUploadSessions` returns the row to the
 *    pending pool on the RUNNING app's recurring cycle on a durable escalating backoff, so a
 *    genuinely recoverable cause — a re-granted share, a tenant repair — still lands the file.
 *  - What actually changes is the promise: the row renders as needing attention, with "local data
 *    was preserved", instead of claiming an automatic retry that cannot currently work (LSP-19325 AC).
 *
 * Deliberately NOT routed to `recover-lost`. That lane mints a replacement target from the retained
 * buffers, and it cannot recover this state: re-initiation reuses the session's stable
 * `idempotencyId`, and contact-api's registry refuses it — a discarded upload's target stub is
 * soft-deleted (`softDeletePendingStub` on the facade abort path), so a re-initiate creates a NEW
 * target row whose id no longer matches the stored `intentFingerprint`
 * (`helpers/uploadSessionRegistry.ts` `assertIntentMatches`), and even reusing the row is refused
 * because `canCreateUploadSessionAttempt` admits only `requested`/`uploading` sessions. Sending the
 * row round that lane would orphan a fresh S3 multipart upload per pass and loop on the same lie.
 */
export class UploadTargetGoneError extends Error {
	cause?: unknown;

	constructor(message: string, options?: { cause?: unknown }) {
		super(message);
		this.name = "UploadTargetGoneError";
		this.cause = options?.cause;
	}
}

/**
 * LSP-21977: a port was asked to address a row that has no server upload yet — only its
 * `pending-target:` placeholder. contact-api rejects that id with a 404 before any controller runs,
 * so the request is not sent at all. `classifyFailure` releases the row without rethrowing: the
 * orphan sweep removes it if its bytes were never saved, and the normal mint path re-runs if they were.
 */
export class NoUploadTargetError extends Error {
	constructor(uploadId: string, operation: string) {
		super(`Upload ${uploadId} has no server target yet; ${operation} was not sent`);
		this.name = "NoUploadTargetError";
	}
}

function assertServerUploadTarget(target: Pick<FileUploadSession, "registryId" | "uploadId">, operation: string): void {
	if (hasNoServerUploadTarget(target)) throw new NoUploadTargetError(target.uploadId, operation);
}

/**
 * Thrown when a completion path has uploaded everything it can locally but the assembled part
 * set is still short/non-contiguous (e.g. a chunk marked `isUploaded` whose etag was lost — the
 * client cannot self-heal it). Completing would truncate the object, so we refuse. The MPU is
 * left open and the server stub stays `pending`, so this case is RECOVERABLE by the server sweep
 * (`completeStrandedMultipartUpload` re-derives parts via S3 ListParts). Callers must therefore
 * treat it as handoff-vs-lost (probe S3), NOT as a hard failure — the shared driver routes
 * this through the injected terminal-recovery policy.
 */
export { TruncatedPartsError };

/**
 * Thrown when a picked File's bytes cannot be read from the device — typically an Android
 * cloud-offloaded file (Google Photos "free up space") or a reference that went stale after
 * the picker closed. The browser raises `NotReadableError`/`SecurityError` on every read of
 * such a file. Retrying the SAME File handle can never succeed, so callers must fail fast and
 * guide the user (download the file to the device, re-pick), NOT retry and NOT treat it as a
 * network error.
 */
export { FileUnreadableError, isFileReadError };

/**
 * The server's `/files/multipart/complete` returns `{ success: true, alreadyComplete: true }`
 * (HTTP 200) when the MPU was already completed (duplicate/raced complete, or a server sweep
 * finished it). Treated as a successful completion.
 */
function isAlreadyCompleteResult(result: unknown): boolean {
	return !!result && typeof result === "object" && (result as { alreadyComplete?: unknown }).alreadyComplete === true;
}

/**
 * The STRING identity of a `/uploads` facade rejection. Shared with contact-async and the shared
 * facade client so all three read the same two fields with the same precedence — see
 * `readUploadFacadeErrorCode` for why both `errorCode` and a string `code` have to be read.
 */
const facadeErrorCode = readUploadFacadeErrorCode;

/**
 * `api.completeMultipartUpload` rejects via `_handleResponse` with the parsed error body for a
 * 409 `{ success: false, code: "UPLOAD_LOST" }` (the body's `code` is preserved because
 * `_handleResponse` only fills `code` when nullish). Detects that rejection shape, and the
 * `/uploads` facade's equivalent `errorCode: "UPLOAD_LOST"` body.
 */
function isUploadLostError(error: unknown): boolean {
	return facadeErrorCode(error) === "UPLOAD_LOST";
}

/**
 * The `/uploads` facade's SETTLING conflicts (`UPLOAD_OPERATION_IN_PROGRESS`,
 * `UPLOAD_MANIFEST_CONFLICT`, `UPLOAD_RECONCILIATION_REQUIRED`) live in the shared package so admin
 * and async classify the identical set. Re-exported here because callers outside this module
 * already import it from the driver.
 */
export { isUploadSettlingConflict };

function isPermanentMultipartUrlRefreshError(error: unknown): boolean {
	try {
		if (!error || typeof error !== "object") return false;
		const numericCode = (error as { code?: unknown }).code;
		const code = facadeErrorCode(error);
		return (
			numericCode === 403 ||
			numericCode === 404 ||
			code === "URL_REFRESH_AUTH_REJECTED" ||
			code === "CAPABILITY_EXPIRED" ||
			code === "OPERATION_NOT_SUPPORTED" ||
			code === "UPLOAD_ATTEMPT_FENCE_INVALID" ||
			code === "UPLOAD_NOT_FOUND" ||
			code === "UPLOAD_LOST"
		);
	} catch {
		return false;
	}
}

function permanentUrlRefreshAuthError(cause: unknown): Error & { code: "URL_REFRESH_AUTH_REJECTED"; cause: unknown } {
	return Object.assign(new Error("Multipart URL refresh was rejected after bounded access-token renewal."), {
		name: "UrlRefreshAuthRejectedError",
		code: "URL_REFRESH_AUTH_REJECTED" as const,
		cause
	});
}

/**
 * A `401` / `AUTH_REQUIRED` from an upload URL refresh is AMBIGUOUS in a way the other permanent
 * codes are not: it can mean "the access token this request carried had gone stale" (recoverable —
 * the auth layer can mint a new one in place) or "this session is revoked" (not recoverable). The
 * permanent verdict is only correct for the second, because "needs-attention" is a TERMINAL session
 * status that the recurring resume cycle never re-picks (only a startup pass requeues those rows),
 * so parking on the first would strand an otherwise resumable upload until the page is reloaded.
 */
function isExpiredAuthUrlRefreshError(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const code = (error as { code?: unknown }).code;
	return code === 401 || code === "AUTH_REQUIRED";
}

/**
 * `auth.getAccessToken` is the discriminator between the two readings above, and it already
 * separates them: it RESOLVES a token while the session is alive, RESOLVES NULL for a terminal
 * login-family/invalid-family failure (it owns the interactive recovery in that case), and THROWS
 * `TransientAuthTokenError` when the renewal failed transiently (offline, Auth0 client not ready).
 * `cacheMode: "off"` forces the refresh-token grant so a cached — possibly already-stale — token
 * cannot answer the question the 401 asked. `redirectOnAuthFailure: false` keeps a background
 * upload from hijacking the user's navigation; the app's own auth surfaces own that.
 *
 * Imported lazily so the upload driver keeps no import-time dependency on the Auth0 client.
 */
async function renewAccessTokenAfterUrlRefreshRejection(): Promise<"renewed" | "session-lost"> {
	const { auth } = await import("@/auth");
	const token = await (
		auth as unknown as { getAccessToken: (options: Record<string, unknown>) => Promise<string | null> }
	).getAccessToken({ cacheMode: "off", redirectOnAuthFailure: false });
	return token ? "renewed" : "session-lost";
}

/**
 * BOUNDED re-auth for a URL refresh rejected with an expired-auth code: at most one forced token
 * renewal and at most one retry. Deliberately not a loop — a 401 that survives a freshly minted
 * token is a real refusal, and it keeps its permanent (needs-attention) classification below.
 */
async function withBoundedUrlRefreshReauth<T>(requestRefresh: () => Promise<T>): Promise<T> {
	try {
		return await requestRefresh();
	} catch (error) {
		if (!isExpiredAuthUrlRefreshError(error)) throw error;
		let renewal: "renewed" | "session-lost";
		try {
			renewal = await renewAccessTokenAfterUrlRefreshRejection();
		} catch (renewalError) {
			if (isTransientAuthTokenError(renewalError)) {
				// Deferred, NOT parked: classifyFailure reads an UploadDeferredError as "retry", so the
				// recurring resume cycle picks the session up again once auth/connectivity recovers.
				throw new UploadDeferredError("Multipart URL refresh deferred until the access token can be renewed.", {
					cause: renewalError
				});
			}
			console.warn(
				"[Upload] Could not renew the access token after an expired-auth URL refresh rejection; keeping the permanent verdict",
				uploadFailureLogDetails(renewalError)
			);
			throw permanentUrlRefreshAuthError(error);
		}
		if (renewal === "session-lost") {
			console.warn(
				"[Upload] Access token renewal reported a lost session after an expired-auth URL refresh rejection; not retrying",
				uploadFailureLogDetails(error)
			);
			throw permanentUrlRefreshAuthError(error);
		}
		try {
			return await requestRefresh();
		} catch (retryError) {
			if (isExpiredAuthUrlRefreshError(retryError)) throw permanentUrlRefreshAuthError(retryError);
			throw retryError;
		}
	}
}

function classifyAdminCompletionError(error: unknown): "lost" | "transient" | "fatal" {
	try {
		// isUploadLost, not isUploadLostError: it also covers the completion identity gate's
		// UPLOAD_SUPERSEDED (see its comment) so a refused-because-replaced completion takes the same
		// reinitialize-from-retained-buffers path a lost target does, instead of falling to "fatal".
		if (error instanceof UploadLostError || isUploadLost(error)) return "lost";
		// Chunked-object refusals (plan 3a D7): parts-incomplete is handled by the shared driver's
		// re-mark path and must never read as lost (which would re-upload the whole file) or fatal; an
		// assembly integrity failure keeps the staged parts server-side and the same /complete may
		// succeed on retry. Above the settling arm because neither carries a settling status.
		//
		// Finding 14: "transient" here is uncapped ON PURPOSE. The CEILING lives in the shared driver
		// (MAX_CONSECUTIVE_ASSEMBLY_INTEGRITY_FAILURES in shared/frontend/uploads/src/
		// partsIncompleteBound.ts), which escalates the row to needs-attention once the integrity
		// failures repeat — so do not add a second bound here; both apps get the one ledger.
		if (isUploadPartsIncompleteError(error) || isChunkedObjectAssemblyIntegrityError(error)) return "transient";
		// A settling 409 is the server saying "not yet", not "never". Grouping it with the other
		// transient causes keeps the completion path from treating it as a terminal failure.
		if (isUploadSettlingConflict(error)) return "transient";
		if (error instanceof UploadDeferredError || isTransientAuthTokenError(error) || isTransientUploadNetworkError(error))
			return "transient";
	} catch (classificationError) {
		console.warn(
			"[Upload] Could not inspect multipart completion error metadata; classifying as fatal",
			uploadFailureLogDetails(classificationError)
		);
		// Unreadable third-party error metadata cannot prove a recoverable or terminal upload outcome.
	}
	return "fatal";
}

function createReadableAdminError(error: unknown): Error {
	try {
		return new Error(error instanceof Error ? error.message : String(error), { cause: error });
	} catch {
		return new Error("Upload failed because the original error details were unreadable.", { cause: error });
	}
}

function normalizeAdminCompletionError(error: unknown): Error {
	try {
		// UploadTargetGoneError is already the normalized form of its own cause: rebuilding it through
		// the generic `facadeCode` branch below would strip the type `classifyFailure` reads, and the
		// verdict would fall back to R-2's UPLOAD_NOT_FOUND pause it was minted to replace (LSP-19325).
		if (error instanceof UploadLostError || error instanceof UploadDeferredError || error instanceof UploadTargetGoneError)
			return error;
		if (isUploadSupersededError(error)) {
			// UploadLostError so the R-1 reinitialize lane picks it up (see isUploadLost), with the
			// facade code stamped into the MESSAGE — that string is all a durable row retains
			// (`FileUploadSession.lastError`), so it is the only way a later re-derivation can tell this
			// apart from a genuine loss.
			return new UploadLostError(
				withFacadeErrorCodeInMessage(
					"Upload completion was refused because this upload target was replaced by a newer one; reinitializing from the retained chunks.",
					error
				)
			);
		}
		if (isUploadLostError(error)) {
			return new UploadLostError("Multipart upload is lost (no assembled object). The file must be re-uploaded. [UPLOAD_LOST]");
		}
		if (isUploadSettlingConflict(error)) {
			// Deferred, not failed: the driver's classifyFailure reads an UploadDeferredError as
			// "retry", so the row goes back to the paced retry pool instead of being announced as a
			// dead upload the user has to redo.
			return new UploadDeferredError(
				withFacadeErrorCodeInMessage("Upload completion deferred while the server finishes settling this upload.", error),
				{ cause: error }
			);
		}
		if (isTransientAuthTokenError(error) || isTransientUploadNetworkError(error)) {
			return new UploadDeferredError("Multipart upload completion deferred until connectivity/auth recovers. Session kept pending.", {
				cause: error
			});
		}
		const facadeCode = facadeErrorCode(error);
		if (facadeCode) {
			// `resolveMultipartCompletionRequest` THROWS whatever this returns, and the driver's outer
			// catch then re-classifies it — so the replacement must keep every field the classifiers
			// read (`code`, `errorCode`, `status`, `httpStatus`). Dropping them here would turn a
			// permanent URL-refresh rejection back into an unclassified "other".
			//
			// `details` is in the allowlist for the same reason (LSP-17653 plan 3a): a chunked-object
			// UPLOAD_PARTS_INCOMPLETE refusal names the ordinals it is still owed in
			// `details.missingOrdinals`, and `readUploadPartsIncompleteOrdinals` reads them off the error
			// the shared driver receives — which is THIS rebuilt error. Dropping `details` left the
			// re-mark path with nothing to re-mark, so the same completion was re-posted forever.
			const source = error as {
				message?: unknown;
				code?: unknown;
				errorCode?: unknown;
				status?: unknown;
				httpStatus?: unknown;
				details?: unknown;
			};
			const originalMessage = typeof source.message === "string" ? source.message : "";
			return Object.assign(
				new Error(withFacadeErrorCodeInMessage(originalMessage || `Upload request failed (${facadeCode}).`, error), {
					cause: error
				}),
				{
					name: error instanceof Error ? error.name : "UploadFacadeRequestError",
					code: source.code,
					errorCode: source.errorCode,
					status: source.status,
					httpStatus: source.httpStatus,
					details: source.details
				}
			);
		}
	} catch {
		// Unreadable third-party error metadata must not prevent a persisted session from being deferred.
		return createReadableAdminError(error);
	}
	// Only a falsy rejection needs a stand-in Error. A truthy non-Error still carries the `code`/
	// `errorCode`/`status`/`httpStatus` fields `classifyFailure` reads, and wrapping it here would
	// strip them and downgrade a definitive HTTP completion failure into an unclassified one.
	return error ? (error as Error) : createReadableAdminError(error);
}

async function requestAdminMultipartCompletion(
	target: Pick<
		FileUploadSession,
		| "uploadId"
		| "s3Key"
		| "bucket"
		| "endpointFamily"
		| "registryId"
		| "attemptId"
		| "generation"
		| "takeGeneration"
		| "targetEnvelopesJson"
		| "transport"
	>,
	parts: { ETag: string; PartNumber: number }[],
	requestApi = api
): Promise<{ alreadyComplete: boolean }> {
	try {
		// Plan 3a: a chunked-object attempt completes with the attempt FENCE only. The server
		// re-derives the manifest from the landed `.part` objects and refuses a body that carries
		// `parts` at all, so the enriched manifest below must not be built for this lane. Read off
		// `transport` alone — a legacy target never carries one, and `endpointFamily` deliberately
		// keeps exactly the two reads it always had (lsp19088RecoveryDiagnostics counts them).
		const isChunkedObject = target.transport === "chunked-object";
		const envelopes =
			target.endpointFamily === "uploads" && !isChunkedObject
				? (JSON.parse(target.targetEnvelopesJson ?? "[]") as { targetId?: string; ordinal?: number }[])
				: [];
		const envelopesByOrdinal = new Map(envelopes.map((envelope) => [envelope.ordinal, envelope]));
		const result =
			target.endpointFamily === "uploads"
				? isChunkedObject
					? await requestApi.completeUploadsUpload(
							target.registryId ?? target.uploadId,
							target.attemptId ?? "",
							target.generation ?? 1
						)
					: await requestApi.completeUploadsUpload(
							target.registryId ?? target.uploadId,
							target.attemptId ?? "",
							target.generation ?? 1,
							parts.map((part) => {
								const envelope = envelopesByOrdinal.get(part.PartNumber);
								if (!envelope?.targetId) throw new Error(`Missing opaque target identity for part ${part.PartNumber}`);
								return { ...part, targetId: envelope.targetId, ordinal: part.PartNumber };
							})
						)
				: await requestApi.completeMultipartUpload(target.uploadId, target.s3Key, target.bucket, parts);
		return { alreadyComplete: isAlreadyCompleteResult(result) };
	} catch (error) {
		// Silent-failure rule (CLAUDE.md "Diagnostic logging when fixing silent failures"): contact-api's
		// `assertCurrentAttempt` answers a STALE ATTEMPT FENCE and a genuinely absent upload with the same
		// conflated 404 UPLOAD_NOT_FOUND, by design (uploads controller, R-2 class 1). Every log this
		// failure produced named the row — fileName, sessionId, uploadId — and none of them named the
		// fence, so "which coordinate did the server actually reject" had to be reconstructed from
		// indirect signals. Name all three coordinates that go on the wire, for every failure class:
		// gating this to non-transient errors would hide exactly the ambiguous cases it exists to settle.
		console.warn("[Upload] Multipart completion request failed", {
			endpointFamily: target.endpointFamily,
			uploadId: target.uploadId,
			registryId: target.registryId,
			attemptId: target.attemptId,
			generation: target.generation,
			submittedParts: parts.length,
			...uploadFailureLogDetails(error)
		});
		if (isUploadSupersededError(error)) {
			// Same typed rejection as lost (so every downstream lane keeps its single lost branch), with an
			// honest message: nothing is lost, this attempt was REPLACED and must be reinitialized.
			throw new UploadLostError(
				`Multipart upload ${target.uploadId} was superseded by a newer upload target for the same file; ` +
					`reinitializing from the retained chunks. [${UPLOAD_SUPERSEDED_ERROR_CODE}]`
			);
		}
		if (isUploadLostError(error)) {
			throw new UploadLostError(`Multipart upload ${target.uploadId} is lost (no assembled object). The file must be re-uploaded.`);
		}
		if (isTransientAuthTokenError(error) || isTransientUploadNetworkError(error)) {
			throw new UploadDeferredError(
				`Multipart upload ${target.uploadId} completion deferred until connectivity/auth recovers. Session kept pending.`,
				{ cause: error }
			);
		}
		// Everything above is a verdict the response itself proved. This is the remainder — a refusal
		// that proves nothing about whether the server already holds the file. Ask before failing, so
		// an assembled upload retires instead of re-completing forever (LSP-19090).
		if (isUploadTerminalFailure(error)) throw tagUploadRefusalTrigger(error, "complete");
		const reconciliation = await readUploadRegistryForCompletionRefusal(target);
		if (reconciliation === "assembled") {
			console.info("[Upload] Completion was refused but the registry reports the object assembled; retiring the upload", {
				uploadId: target.uploadId,
				registryId: target.registryId,
				attemptId: target.attemptId,
				generation: target.generation
			});
			return { alreadyComplete: true };
		}
		// LSP-19325: the refusal named this upload as not-found AND the ungated projection read agrees.
		// Two independent not-founds are not "we could not tell" — they are the server stating that
		// these coordinates address nothing, which is the one thing a retry of them can never repair.
		// Without this the refusal fell to R-2's `UPLOAD_NOT_FOUND` pause and the row re-sent the same
		// dead presigned URL and S3 upload id indefinitely under "We'll keep retrying automatically."
		// See UploadTargetGoneError for why this is needs-attention rather than a discard or a reinit.
		if (reconciliation === "absent" && facadeErrorCode(error) === "UPLOAD_NOT_FOUND") {
			throw new UploadTargetGoneError(
				// `withFacadeErrorCodeInMessage`, not a hand-stamped suffix: the durable row keeps only
				// `FileUploadSession.lastError`, and this is the shared helper every other completion
				// verdict in this file uses to put the facade's own code into that string.
				withFacadeErrorCodeInMessage(
					`Upload ${target.uploadId} no longer exists on the server: its completion and the upload registry read ` +
						`both reported it absent. The retained local file data was preserved.`,
					error
				),
				{ cause: error }
			);
		}
		throw error;
	}
}

const TRANSIENT_UPLOAD_NETWORK_MESSAGE_FRAGMENTS = [
	"load failed",
	"network connection",
	"internet connection",
	"failed to fetch",
	"networkerror when attempting to fetch",
	"request did not go through",
	"appear to be offline"
] as const;

const S3_PUT_HTTP_FAILURE_STATUS_PATTERN = /\bS3 PUT\s+(4\d\d|5\d\d)\b/i;
const S3_UPLOAD_TRANSPORT_ERROR_CODES = new Set(["NetworkError", "AbortError"]);
const S3_UPLOAD_SETUP_FAILURE_MESSAGE_FRAGMENTS = [
	"failed to execute 'open' on 'xmlhttprequest'",
	"failed to execute 'send' on 'xmlhttprequest'",
	"invalid url"
] as const;

function isS3UploadError(error: unknown): error is Error & { code?: unknown } {
	return error instanceof Error && error.name === "S3UploadError";
}

function isS3UploadTransportError(error: unknown): boolean {
	if (!isS3UploadError(error)) return false;
	const code = (error as { code?: unknown }).code;
	if (code === "NetworkError") {
		const message = error.message.toLowerCase();
		if (S3_UPLOAD_SETUP_FAILURE_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment))) {
			return false;
		}
	}
	return typeof code === "string" && S3_UPLOAD_TRANSPORT_ERROR_CODES.has(code);
}

function isS3UploadHttpFailure(error: unknown): boolean {
	return isS3UploadError(error) && !isS3UploadTransportError(error);
}

function hasHttpFailureStatus(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const candidate = error as { code?: unknown; status?: unknown; statusCode?: unknown; response?: { status?: unknown } };
	const statusCandidates = [candidate.status, candidate.statusCode, candidate.response?.status, candidate.code];
	if (statusCandidates.some((status) => typeof status === "number" && status >= 400 && status <= 599)) {
		return true;
	}
	if (isS3UploadHttpFailure(error)) {
		return true;
	}
	if (error instanceof Error) {
		return S3_PUT_HTTP_FAILURE_STATUS_PATTERN.test(error.message);
	}
	return false;
}

function isNetworkShapedTypeError(error: unknown): boolean {
	if (!(error instanceof TypeError)) return false;
	const message = error.message.toLowerCase();
	return TRANSIENT_UPLOAD_NETWORK_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment));
}

export function isTransientUploadNetworkError(error: unknown): boolean {
	// isUploadLost covers UploadLostError, the raw 409 UPLOAD_LOST body, and the identity gate's
	// UPLOAD_SUPERSEDED. None of the three is a network condition, and misreading one as transient would
	// make `isUploadDeferredError` claim it first — classifying it "retry" and re-posting the identical
	// (permanently refused) completion forever.
	if (error instanceof UploadCancelledError || isUploadLost(error) || error instanceof FileUnreadableError) {
		return false;
	}
	if (error instanceof ChunkNetworkError) return true;
	if (error instanceof AmbiguousUploadTargetError) return true;
	if (isS3UploadTransportError(error)) return true;
	if (hasHttpFailureStatus(error)) return false;
	if (isNetworkShapedTypeError(error)) return true;
	if (error instanceof Error && error.name === "AbortError") return true;
	return false;
}

/**
 * Unified "is this a confirmed-lost upload?" check for all completion paths. Returns true for
 * BOTH shapes the lost outcome can take:
 *   - a thrown `UploadLostError` (service paths complete through the shared upload driver), and
 *   - a raw 409 `{ code: "UPLOAD_LOST" }` rejection classified by the shared
 *     completionTransition path.
 *
 * Use this everywhere instead of re-deriving the check per call site — the lost semantics (stop
 * retrying THIS target, reinitialize from the retained buffers, and surface "restarting" rather
 * than "re-upload") must stay identical across the dialog and every resume completion path.
 */
export function isUploadLost(error: unknown): boolean {
	// UPLOAD_SUPERSEDED belongs in exactly this bucket, not next to it. The server refused the
	// completion because the durable row now points at a REPLACEMENT upload on the same key (contact-api
	// completion identity gate, the second half of the R-1 fix) — so this uploadId can never complete,
	// which is the same verdict as lost, while the local buffers are untouched. Routing it here gives it
	// the R-1 lost lane verbatim: `classifyFailure` → "recover-lost" → `recoverLostPersistedSession`
	// reinitializes against a fresh target from the retained chunks. Adding a parallel classification
	// would only risk one of the two lanes drifting into a discard.
	return error instanceof UploadLostError || isUploadLostError(error) || isUploadSupersededError(error);
}

export function isUploadDeferredError(error: unknown): boolean {
	return error instanceof UploadDeferredError || isTransientAuthTokenError(error) || isTransientUploadNetworkError(error);
}

export function isUploadSessionDeliverableByResume(status: FileUploadSession["status"]): boolean {
	return status === "pending" || status === "queued" || status === "uploading";
}

/**
 * Probes S3 (via ListParts) for a multipart upload's recoverability when the local session can't be
 * resumed (IDB unreadable / chunks missing). Returns:
 *  - gone: true — the MPU no longer exists (server translated NoSuchUpload/NoSuchKey → { gone: true }).
 *    It was either ALREADY COMPLETED (an assembled object may exist — let the server sweep/finalize and
 *    its objectExists check be the backstop) or never persisted. Gone NEVER proves loss.
 *  - complete: true — the MPU is still open AND already holds a contiguous 1..totalChunks part set
 *    (the server sweep can finish it).
 * A NON-gone, NON-complete result (some parts but not all, or zero parts on a still-open MPU) is the only
 * state that proves the client can't finish → the caller treats it as genuinely lost.
 * Throws if the ListParts call itself fails (transient) — the caller decides the fallback.
 * Callers MUST treat (gone || complete) as hand off to the server, and only (!gone && !complete) as LOST.
 *
 * LSP-17796: also throws AmbiguousListPartsError when the server flags the listing anomalous. Returning
 * { complete: false, gone: false } there would be read as the ONE state this probe treats as proof of loss,
 * off a manifest the server explicitly could not vouch for.
 */
export async function probeMultipartParts(
	uploadId: string,
	s3Key: string,
	bucket: string,
	totalChunks: number
): Promise<{ complete: boolean; gone: boolean }> {
	const evidence = listPartsEvidenceFromResponse(uploadId, await api.listMultipartParts(uploadId, s3Key, bucket));
	if (evidence.kind === "gone") {
		return { complete: false, gone: true };
	}
	if (evidence.kind === "assembled") return { complete: true, gone: false };
	const sorted = [...evidence.parts]
		.map((part: { partNumber: number }) => ({ PartNumber: part.partNumber }))
		.sort((a, b) => a.PartNumber - b.PartNumber);
	return { complete: hasContiguousParts(sorted, totalChunks), gone: false };
}

function createEphemeralAdminCompletionSession(
	input: NewSessionInput & { ephemeral: true; target: FamilyAwareMultipartUploadTarget }
): FileUploadSession & { ephemeral: true } {
	const opaque = isUploadsUploadTarget(input.target);
	return {
		id: `unpersisted:${uploadTargetOpaqueId(input.target)}`,
		ephemeral: input.ephemeral,
		conversationId: input.conversationId,
		fileName: input.fileName,
		fileSize: input.fileSize,
		fileType: input.fileType,
		uploadMethod: "multipart",
		fileCategory: input.fileCategory ?? inferFileCategory(input.fileType),
		uploadId: uploadTargetOpaqueId(input.target),
		s3Key: opaque ? "" : (input.target as Extract<FamilyAwareMultipartUploadTarget, { family: "legacy" }>).s3Key,
		bucket: opaque ? "" : (input.target as Extract<FamilyAwareMultipartUploadTarget, { family: "legacy" }>).bucket,
		totalChunks: input.totalChunks,
		chunkUrlsJson: JSON.stringify(uploadTargetChunkUrls(input.target)),
		urlCreatedAt: Date.now(),
		status: "uploading",
		createdAt: Date.now(),
		cloudHosted: false,
		endpointFamily: opaque ? "uploads" : "legacy",
		...(opaque
			? {
					registryId: (input.target as Extract<FamilyAwareMultipartUploadTarget, { family: "uploads" }>).registryId,
					attemptId: (input.target as Extract<FamilyAwareMultipartUploadTarget, { family: "uploads" }>).attemptId,
					generation: (input.target as Extract<FamilyAwareMultipartUploadTarget, { family: "uploads" }>).generation,
					targetEnvelopesJson: JSON.stringify(
						(input.target as Extract<FamilyAwareMultipartUploadTarget, { family: "uploads" }>).envelopes
					),
					// Plan 3a: an unpersisted direct-upload completion reads `transport` off THIS row, so a
					// chunked-object attempt that never touched IndexedDB must carry it too — otherwise its
					// completion would send the parts body the server refuses. Same projection rule as the
					// durable writes in adminSessionStore.
					...chunkedObjectLaneColumns(input.target as { transport?: string; chunkedObject?: ChunkedObjectTargetBlock })
				}
			: {})
	};
}

const adminConnectivity = createBrowserConnectivityPort();

const MAX_CHUNK_RETRIES = 3;
const CHUNK_CONCURRENCY = 3;
// LSP-15716/LSP-16654: the zero-progress stall threshold (CHUNK_STALL_TIMEOUT_MS) now lives in
// helpers/s3.ts as the single shared constant for every browser→S3 presigned-PUT transport, and is
// imported above. A slow-but-progressing upload keeps resetting the timer and is never aborted;
// only genuine no-progress stalls trip it.
/** How long to wait for a 1-byte read before assuming the file is cloud-hosted. */
const CLOUD_DETECT_TIMEOUT_MS = 150;

/**
 * Maximum file size to load entirely into memory for cloud-hosted files.
 * Above this threshold we fall back to chunked reading to avoid OOM crashes,
 * accepting the re-download penalty for large cloud files.
 */
const MAX_CLOUD_ARRAYBUFFER_BYTES = 500 * 1024 * 1024; // 500 MB

export function inferFileCategory(mimeType: string): FileCategory {
	if (mimeType.startsWith("image/")) return "image";
	if (mimeType.startsWith("video/")) return "video";
	return "document";
}

// "lost" is deliberately absent: under R-1 its shared branch (discardIfUploadIdMatches) is the
// destroy admin no longer performs, so nothing here may classify a failure into it.
type AdminResumeFailureKind =
	| "retry"
	| "retry-slow"
	| "recover-lost"
	| "terminal-read-failure"
	| "terminal-local-gaps"
	| "needs-attention"
	| "other";

type AdminDriverPorts = ChunkedUploadDriverPorts<File, FileUploadSession, unknown, AdminResumeFailureKind>;

const adminTerminalRecoveryPolicy = createPersistedAdminTerminalRecoveryPolicy();

function createAdminTargetValidationError(failure: MultipartTargetValidationFailure, file: File): Error {
	if (failure.reason === "empty-file") return new Error("Cannot upload empty files");
	if (failure.reason === "invalid-urls") return new Error(`Upload of "${file.name}" aborted: server returned an invalid chunk URL`);
	if (failure.reason === "invalid-upload-id") return new Error(`Upload of "${file.name}" aborted: server returned an invalid uploadId`);
	if (failure.reason === "invalid-s3-key") return new Error(`Upload of "${file.name}" aborted: server returned an invalid s3Key`);
	if (failure.reason === "invalid-bucket") return new Error(`Upload of "${file.name}" aborted: server returned an invalid bucket`);
	return new Error(
		`Upload of "${file.name}" aborted: server returned ${failure.targetChunkCount} chunk URL(s) but the file needs ${failure.requiredChunks} ` +
			`(${formatFileSize(file.size)} at ${formatFileSize(CHUNK_SIZE)}/chunk). Refusing to upload to avoid a truncated file.`
	);
}

function toAdminFileUploadChunkMeta(session: FileUploadSession, chunk: DriverChunkMeta): FileUploadChunkMeta {
	return {
		sessionId: session.id,
		chunkIndex: chunk.chunkIndex,
		isUploaded: chunk.isUploaded ? 1 : 0,
		etag: chunk.etag,
		size: chunk.size
	};
}

export function createAdminChunkedUploadDriverPorts(): AdminDriverPorts {
	const ports = {
		createEphemeralSession: createEphemeralAdminCompletionSession,
		sessionStore: {
			getById: (sessionId) => adminSessionStore.getById(sessionId),
			findByUploadId: (uploadId) => adminSessionStore.findByUploadId(uploadId),
			createSession: (input, leaseToken) => adminSessionStore.createSession(input, leaseToken),
			persistChunk: (session, chunk) => adminSessionStore.persistChunk(session, chunk),
			finishPrePersist: (session) => adminSessionStore.finishPrePersist(session),
			abandonSetup: (session) => adminSessionStore.abandonSetup(session),
			deleteAllChunkData: (session) => adminSessionStore.deleteAllChunkData(session),
			releasePending: (session, reason, options) => adminSessionStore.releasePending(session, reason, options),
			markTerminalComplete: (session) => adminSessionStore.markTerminalComplete(session),
			markBytesGone: (session, reason) => adminSessionStore.markBytesGone(session, reason),
			tombstoneCancelled: (session, options) =>
				adminSessionStore.tombstoneCancelled(session, { retainChunks: true, notifyOtherTabs: options?.notifyOtherTabs }),
			markChunkUploaded: (session, chunkIndex, etag) => adminSessionStore.markChunkUploaded(session, chunkIndex, etag),
			// Plan 3a D7: the ordinals a chunked-object /complete said it is still owed go back to pending
			// so the ordinary chunk loop re-sends exactly those, never the whole file.
			markChunksNotUploaded: (session, chunkIndexes) => adminSessionStore.markChunksNotUploaded!(session, chunkIndexes),
			cleanupCompleted: (session) => adminSessionStore.cleanupCompleted(session),
			discardIfUploadIdMatches: (sessionId, uploadId) => adminSessionStore.deleteSessionByIdIfUploadIdMatches(sessionId, uploadId),
			verifyReinitializable: (session) => adminSessionStore.verifyReinitializable(session),
			listChunkMeta: async (session) =>
				(await adminSessionStore.listChunkMeta(session)).map((chunk) => ({ ...chunk, isUploaded: Boolean(chunk.isUploaded) })),
			putChunkMeta: (session, chunk, options) =>
				adminSessionStore.putChunkMeta(session, toAdminFileUploadChunkMeta(session, chunk), options),
			readChunkData: (session, chunkIndex) => adminSessionStore.readChunkData(session, chunkIndex),
			markNeedsAttention: (session, reason, failure) => adminSessionStore.markNeedsAttention(session, reason, failure),
			mergeAndPersistUrls: (session, updates, fallbackUrls, refreshesAllPendingUrls, targetMigration) =>
				adminSessionStore.mergeAndPersistUrls(session, updates, fallbackUrls, refreshesAllPendingUrls, targetMigration),
			attachTarget: (session, target, _leaseToken, retainOwner) => adminSessionStore.attachTarget(session, target, retainOwner),
			resetForReinitialization: (session, target) => adminSessionStore.resetForReinitialization(session, target)
		} satisfies AdminDriverPorts["sessionStore"],
		leaseCoordinator: {
			createScope: (purpose, sessionId, requestedToken) => createAdminLeaseScope(purpose, sessionId, requestedToken)
		},
		chunkSources: createAdminChunkSourceFactory({
			chunkSize: CHUNK_SIZE,
			cloudProbeTimeoutMs: CLOUD_DETECT_TIMEOUT_MS,
			readChunkData: (session, chunkIndex) => adminSessionStore.readChunkData(session, chunkIndex),
			deleteAllChunkData: (session) => adminSessionStore.deleteAllChunkData(session)
		}),
		chunkTransport: createAdminS3ChunkTransport(),
		multipartApi: {
			async progress(target, progress, options) {
				const requestApi = scopedAdminUploadApi(api, target);
				if (target.endpointFamily !== "uploads") return;
				assertServerUploadTarget(target, "progress");
				await requestApi.postUploadsProgress(
					target.registryId ?? target.uploadId,
					target.attemptId ?? "",
					target.generation ?? 1,
					progress,
					options?.signal
				);
			},
			async complete(target, parts) {
				assertServerUploadTarget(target, "complete");
				return requestAdminMultipartCompletion(target, [...parts], scopedAdminUploadApi(api, target));
			},
			async listParts(target, persistenceFence) {
				const requestApi = scopedAdminUploadApi(api, target);
				assertServerUploadTarget(target, "listParts");
				const response =
					target.endpointFamily === "uploads"
						? await requestApi
								.listUploadsParts(target.registryId ?? target.uploadId, target.attemptId ?? "", target.generation ?? 1)
								.catch((error: unknown) => {
									throw tagUploadRefusalTrigger(error, "parts");
								})
						: await requestApi.listMultipartParts(target.uploadId, target.s3Key, target.bucket);
				if (announcesMultipartAttemptMigration(target, response)) {
					await preparePersistedUploadUrls({
						session: target,
						pendingChunkIndexes: Array.from({ length: target.totalChunks }, (_value, index) => index),
						fallbackUrls: parsePersistedChunkUrls(target.chunkUrlsJson),
						ports,
						lease: persistenceFence?.lease,
						ownsCurrentSession: persistenceFence?.ownsCurrentSession,
						forceRefresh: true
					});
				}
				// LSP-17796: fails closed on `anomalous: true` (and on a missing parts array) so an UNRESOLVED
				// listing can never be handed to the terminal-recovery policy as verified-empty evidence, which
				// it would answer with RESUME_OUTCOME.LOST plus deletion of the local session.
				return listPartsEvidenceFromResponse(target.uploadId, response);
			},
			async refreshUrls(target, partNumbers, useRegionalEndpoint = false) {
				const requestApi = scopedAdminUploadApi(api, target);
				assertServerUploadTarget(target, "refreshUrls");
				return await withBoundedUrlRefreshReauth(async () => {
					if (target.endpointFamily === "uploads") {
						let response = useRegionalEndpoint
							? await requestApi.refreshUploadsUrls(
									target.registryId ?? target.uploadId,
									target.attemptId ?? "",
									target.generation ?? 1,
									[...partNumbers],
									true
								)
							: await requestApi.refreshUploadsUrls(
									target.registryId ?? target.uploadId,
									target.attemptId ?? "",
									target.generation ?? 1,
									[...partNumbers]
								);
						const registryId = target.registryId ?? target.uploadId;
						if (response?.id !== undefined && response?.id !== registryId) {
							throw new MultipartUrlRefreshError("Uploads URL refresh returned a different registry target");
						}
						if (announcesMultipartAttemptMigration(target as unknown as MultipartSessionShape, response)) {
							const responseAttemptId = response.attemptId;
							if (UPLOAD_ATTEMPT_UUID_PATTERN.test(registryId) && !UPLOAD_ATTEMPT_UUID_PATTERN.test(responseAttemptId)) {
								throw new MultipartUrlRefreshError(
									"Uploads URL refresh returned an invalid attempt migration announcement"
								);
							}
							const responseGeneration = response.generation;
							const responseKind = response.kind;
							const responseTransport = response.transport;
							const responseStatus = response.status;
							const responseTakeGeneration = response.attempt?.takeGeneration;
							const responseAttemptTransport = response.attempt?.transport;
							const totalChunks = target.totalChunks;
							if (!Number.isSafeInteger(totalChunks) || (totalChunks ?? 0) <= 0) {
								throw new MultipartUrlRefreshError(
									"Uploads URL refresh returned an invalid attempt migration announcement"
								);
							}
							const allPartNumbers = Array.from({ length: totalChunks! }, (_value, index) => index + 1);
							response = useRegionalEndpoint
								? await requestApi.refreshUploadsUrls(
										registryId,
										responseAttemptId,
										responseGeneration,
										allPartNumbers,
										true
									)
								: await requestApi.refreshUploadsUrls(registryId, responseAttemptId, responseGeneration, allPartNumbers);
							if (
								response?.id !== registryId ||
								response?.attemptId !== responseAttemptId ||
								response?.generation !== responseGeneration ||
								response?.kind !== responseKind ||
								response?.transport !== responseTransport ||
								response?.status !== responseStatus ||
								response?.attempt?.id !== responseAttemptId ||
								response?.attempt?.generation !== responseGeneration ||
								response?.attempt?.transport !== responseAttemptTransport ||
								response?.attempt?.takeGeneration !== responseTakeGeneration ||
								!Array.isArray(response?.targets)
							) {
								throw new MultipartUrlRefreshError("Uploads URL refresh migration response changed identity");
							}
							const replacementTarget = createUploadsMultipartUploadTarget({
								registryId,
								attemptId: responseAttemptId,
								generation: responseGeneration,
								takeGeneration: responseTakeGeneration,
								envelopes: response.targets as UploadTargetEnvelope[]
							});
							if (replacementTarget.envelopes.length !== totalChunks) {
								throw new MultipartUrlRefreshError("Uploads URL refresh migration did not return every target envelope");
							}
							return Object.assign(
								replacementTarget.envelopes.map((envelope) => ({
									partNumber: envelope.ordinal,
									url: envelope.url,
									envelope
								})),
								{ replacementTarget }
							);
						}
						if (!Array.isArray(response?.targets)) return [];
						let persisted: unknown;
						try {
							persisted = JSON.parse(target.targetEnvelopesJson ?? "");
						} catch {
							throw new Error("Uploads URL refresh requires valid persisted target envelopes");
						}
						if (!Array.isArray(persisted)) throw new Error("Uploads URL refresh requires valid persisted target envelopes");
						const persistedByOrdinal = new Map(
							(persisted as UploadTargetEnvelope[]).map((envelope) => [envelope.ordinal, envelope])
						);
						const refreshedByOrdinal = new Map(
							(response.targets as UploadTargetEnvelope[]).map((envelope) => [envelope.ordinal, envelope])
						);
						// Rebuilt through `persistedUploadTarget` so the row's minted `transport` survives the
						// refresh — a bare rebuild reads back as multipart and would silently demote a resumed
						// chunked-object attempt into one that completes with a parts body.
						const normalized = persistedUploadTarget({
							endpointFamily: "uploads",
							registryId: target.registryId ?? "",
							attemptId: target.attemptId ?? "",
							generation: target.generation ?? 1,
							takeGeneration: target.takeGeneration ?? "",
							targetEnvelopesJson: JSON.stringify(
								(persisted as UploadTargetEnvelope[]).map(
									(envelope) => refreshedByOrdinal.get(envelope.ordinal) ?? envelope
								)
							),
							transport: target.transport
						});
						// `persistedUploadTarget` answers undefined only for a non-`uploads` family row.
						// A silent `!` here would surface as a bare "cannot read envelopes" mid-refresh;
						// name the attempt instead so the diagnostic log says which row was mis-familied.
						if (!normalized) {
							throw new Error(
								`Uploads URL refresh could not rebuild target for attempt ${target.attemptId ?? "(no attemptId)"}: persisted row is not an uploads-family target`
							);
						}
						const normalizedByOrdinal = new Map(normalized.envelopes.map((envelope) => [envelope.ordinal, envelope]));
						return response.targets.map((candidate: unknown) => {
							const ordinal = (candidate as { ordinal?: unknown })?.ordinal;
							if (typeof ordinal !== "number" || !Number.isSafeInteger(ordinal) || ordinal < 1) {
								throw new Error("Uploads URL refresh returned an invalid target envelope");
							}
							const previous = persistedByOrdinal.get(ordinal);
							const envelope = normalizedByOrdinal.get(ordinal);
							if (!previous || !envelope) throw new Error("Uploads URL refresh returned an unknown target ordinal");
							if (envelope.targetId !== previous.targetId) {
								throw new Error("Uploads URL refresh target fence does not match the pinned session");
							}
							return { partNumber: envelope.ordinal, url: envelope.url, envelope };
						});
					}
					const response = await requestApi.refreshMultipartUrls(target.uploadId, target.s3Key, target.bucket, [...partNumbers]);
					const urls: string[] = Array.isArray(response?.urls) ? response.urls : [];
					return urls.map((url, index) => ({ partNumber: partNumbers[index], url }));
				});
			}
		} satisfies AdminDriverPorts["multipartApi"],
		persistedTargetInitiator: createAdminPersistedTargetInitiator(),
		persistedResume: {
			classifyData: (data) => (data === undefined || data === null || data.byteLength === 0 ? "missing" : "available"),
			reconcileReadFailure: isIdbInternalError,
			reconciliationDefer: "retry",
			prepareUpload: async ({ session }) => ({
				kind: "ready",
				chunkSource: {
					read: async (chunkIndex: number) => {
						const buffer = await getChunkBuffer(session.id, chunkIndex);
						if (!buffer) throw new Error(`Chunk buffer missing for session ${session.id} chunk ${chunkIndex}`);
						return { ok: true as const, blob: new Blob([buffer]) };
					}
				},
				createTruncatedPartsError: (uploadedPartCount: number) =>
					new TruncatedPartsError(
						`Resume of "${session.fileName}" aborted before completion: have ${uploadedPartCount}/${session.totalChunks} part etags ` +
							`(expected contiguous 1..${session.totalChunks}). Refusing to complete a truncated object; session stays recoverable.`
					)
			}),
			classifyFailure(error) {
				if (isUploadTerminalFailure(error)) return "needs-attention";
				if (isUploadDeferredError(error) || error instanceof NoUploadTargetError) return "retry";
				// Plan 3a D7: BEFORE the lost arm. Both chunked-object refusals are recoverable in place —
				// the shared driver re-marks the named ordinals and the chunk loop re-sends exactly those —
				// so neither may reach "recover-lost" (a whole new target and a full re-upload) or "other"
				// (release AND rethrow, which the tray announces as a failed upload).
				// Finding 14: the assembly-integrity half of this is bounded in the shared driver
				// (MAX_CONSECUTIVE_ASSEMBLY_INTEGRITY_FAILURES), which escalates the row before this
				// classifier is consulted again. Keep this arm unconditional; the ceiling is not here.
				if (isUploadPartsIncompleteError(error) || isChunkedObjectAssemblyIntegrityError(error)) return "retry";
				// R-1 ("there is no destroy", owner directive 2026-08-26). This used to return "lost",
				// whose shared branch runs `discardIfUploadIdMatches` — deleting an intact durable session
				// and its retained chunk buffers on the strength of a SERVER verdict about the target,
				// then telling the user to re-upload. "recover-lost" routes the same verdict into
				// `recoverLostPersistedSession`, which reinitializes the row against a freshly minted
				// target when the local buffers are all still there (contact-async's long-standing
				// behavior), and reaches a terminal bytes-gone discard only when they genuinely are not.
				if (isUploadLost(error)) return "recover-lost";
				// Every OTHER facade 409 is the server settling, not failing. Before the API serialized
				// `errorCode` these were indistinguishable from an unknown error, so they fell to
				// "other" — which releases the row to pending AND rethrows, making the drain announce a
				// bare "Upload failed" on a file the server was in the middle of finishing. "retry"
				// releases the row without the throw, so the paced drain simply comes back.
				if (isUploadSettlingConflict(error)) {
					console.warn("[Upload] Upload is still settling on the server; will keep retrying", {
						facadeErrorCode: facadeErrorCode(error),
						...uploadFailureLogDetails(error)
					});
					return "retry";
				}
				// LSP-19912: the CEILING on the arm directly above. `retry` releases the row to the paced
				// drain WITHOUT rethrowing, so a permanently wedged `sealed`/`processing` registry owner
				// kept this durable large-file/video session re-initiating forever with no user-visible
				// escape. `createAdminPersistedTargetInitiator` mints this client-side code once the row's
				// durable settling window (`FileUploadSession.settlingConflictSince`) outlives
				// `SETTLING_CONFLICT_CEILING_MS`. Named explicitly rather than left to fall through: this
				// classifier is its own ladder and never consults `classifyUploadFailure` (which does call
				// the code terminal), so an unnamed code lands in the trailing `"other"` — release AND
				// rethrow — which announces a bare "Upload failed" and lets the drain come straight back.
				// The park is what makes the verdict stick: the retained bytes, the tray row and the
				// user's Retry/Remove all survive, and `isEvidenceTerminalFileUploadError` reads the code
				// back out of the persisted `lastError` so a reload does not un-park it.
				if (facadeErrorCode(error) === UPLOAD_SETTLING_EXPIRED_CODE) {
					console.warn("[Upload] The server never stopped reporting this upload as settling; parking it for the user", {
						facadeErrorCode: UPLOAD_SETTLING_EXPIRED_CODE,
						...uploadFailureLogDetails(error)
					});
					return "needs-attention";
				}
				// LSP-19325: BEFORE isPermanentMultipartUrlRefreshError, whose numeric-404 /
				// UPLOAD_NOT_FOUND arms would otherwise claim this and return "retry-slow" — R-2's
				// deliberate pause for a code that cannot distinguish "gone" from "not permitted right
				// now". This error is only minted once the ungated registry read has independently
				// confirmed the upload does not exist, so the pause has nothing left to wait for and the
				// row must stop advertising an automatic retry. Bytes are retained and the recurring
				// requeue cycle still comes back for it — see UploadTargetGoneError.
				if (error instanceof UploadTargetGoneError) return "needs-attention";
				if (isPermanentMultipartUrlRefreshError(error)) return "retry-slow";
				if (isIdbInternalError(error)) return "terminal-read-failure";
				// LSP-18742: a successful PUT whose ETag was stripped is retryable, but only on the slow tier;
				// fast retries spun one production upload for 2h38m, while terminalizing it falsely asked the
				// user to act. Preserve the bytes and keep the session schedulable without hot-looping.
				if (error instanceof MissingEtagError) return "retry-slow";
				if (error instanceof TruncatedPartsError) return "terminal-local-gaps";
				// LSP-19620: the OBJECT STORE gave a deterministic rejection of these exact bytes by this
				// exact request, which no number of retries can change. Two conditions, both required:
				//
				//  - `S3HttpError` — the class the chunk PUT transport mints, and the only evidence here
				//    that the failing request was a part PUT rather than an API call. Deliberately NOT any
				//    error whose `code` happens to be a 4xx: `/complete` answering 400 is an API verdict
				//    about a MANIFEST, and the completion path (whose failures reach this same classifier)
				//    must keep releasing such a row to `pending` so the next pass can re-list its parts and
				//    complete with a fresh manifest. Widening past the object store broke exactly that
				//    invariant in `chunkedUploadService.spec.ts`.
				//  - `isDeterministicObjectStoreRejection` — it excludes the store's transport-shaped codes
				//    (a dropped mid-body PUT is not a verdict about the content) and a 400 whose body
				//    carries no parseable code at all, through the shared `isTransportShapedS3Failure`
				//    that the foreground lane reads too;
				//  - which in turn requires the shared classifier to call it terminal, naming R-2's 4xx rule (400
				//    malformed, 410 Gone, 413 too large, 422 semantically rejected) and deliberately leaves
				//    every other 4xx opaque-and-retryable. So this arm claims only what the platform has
				//    positive permanence evidence for, and `kind === "terminal"` (never `terminal === true`)
				//    keeps cancellation — also terminal — on its own path through this driver.
				//
				// Before this arm such a rejection fell through to "other", which releases the row to
				// pending AND rethrows. The throw lands in the drain's `onSessionError`, which marks the row
				// "we'll keep retrying automatically" and leaves it resume-deliverable, so the paced drain
				// comes straight back: 82 and 117 rejected part PUTs across two 15-minute staging runs. And
				// because only a PARK feeds `evidencedFailureCount`, the connectivity-gated retry ceiling
				// could never be approached on this path at all. Routing the verdict into the driver's
				// EXISTING needs-attention branch is what makes that ceiling reachable — the row parks with
				// its bytes retained, the evidence ledger moves, the requeue keeps coming back on the slow
				// tier, and the tray shows a row the user can act on instead of promising a retry that
				// cannot work. `onSessionError`'s own reasoning is untouched: running out of an attempt
				// ladder is still a STATUS, not a verdict. This is the other thing — a verdict.
				//
				// It does not compete with the drain's own park for a confirmed migration-integrity error
				// (LSP-19805). That class is a `MultipartUrlRefreshError`, never an `S3HttpError`, and the
				// shared driver rethrows it BEFORE consulting this classifier — so it reaches
				// `onSessionError`, which owns its reason and its exact-claim terminal handoff. Asserted
				// in `lsp19620-permanent-verdict-parks.spec.ts`, both directions.
				if (error instanceof S3HttpError && isDeterministicObjectStoreRejection(error)) {
					// Silent-failure rule: this is the moment automatic progress stops being possible, and
					// it is invisible until the user opens the tray. Name the verdict in Datadog.
					console.warn("[Upload] The storage rejected this upload permanently; parking it for the user", {
						...uploadFailureLogDetails(error)
					});
					return "needs-attention";
				}
				return "other";
			}
		},
		policies: {
			// R-1: "reinitialize" is what lets `recoverLostPersistedSession` mint a replacement target
			// from the retained buffers instead of failing fast into a discard. It also stops the
			// direct-upload path from skipping its release-to-pending on a `lost` classification, so a
			// foreground UPLOAD_LOST now hands the row to the paced drain (which reinitializes it)
			// rather than leaving it "uploading" for the dialog to delete.
			lostRecoveryPolicy: "reinitialize",
			// Orthogonal to the lost policy above, and deliberately unchanged by R-1: a chunk PUT that
			// proves the MPU is gone on S3 is still probed for an already-assembled object first. That
			// probe RETIRES a session the server already holds; without it the same file would be
			// re-uploaded against a fresh target. Before `goneMultipartRecovery` existed this behavior
			// rode on `lostRecoveryPolicy === "fail-fast"`, so flipping the lost policy would have
			// silently taken it away.
			goneMultipartRecovery: "probe-complete",
			directUploadQuotaPolicy: "continue-without-resume",
			terminalRecovery: {
				async decide(context) {
					const outcome = await recoverAdminTerminalSession(context, adminTerminalRecoveryPolicy);
					// LSP-19428: COMPLETED must be mapped explicitly. The policy started returning it for
					// an already-assembled object, and this port's trailing `return "retry"` is a
					// fall-through, not an exhaustive switch — so an unmapped outcome silently becomes a
					// retry instruction. That is not merely wrong copy: the policy has ALREADY deleted the
					// local session by the time it reports a terminal verdict, so `"retry"` sends the
					// driver into `releasePersistedSessionForRetry` (chunkedUploadDriver isRetryOutcome →
					// release) for a row with no local chunk data left, and the paced drain then re-attempts
					// it and reports LOST — telling the user to re-upload a file the server already holds.
					// Worse still at adminResumeSession.ts:28, where a `"retry"` on a recovery-blocked row
					// becomes RECOVERY_BLOCKED. Any new RESUME_OUTCOME member must be added here too.
					if (outcome === RESUME_OUTCOME.COMPLETED) return "completed";
					if (outcome === RESUME_OUTCOME.HANDED_OFF) return "handed-off";
					if (outcome === RESUME_OUTCOME.LOST) return "lost";
					if (outcome === RESUME_OUTCOME.NEEDS_ATTENTION) return "needs-attention";
					if (outcome === RESUME_OUTCOME.RECOVERY_BLOCKED) return "recovery-blocked";
					return "retry";
				}
			},
			cancelEnabled: true,
			probeAlreadyCompleteWithNoParts: true
		} satisfies AdminDriverPorts["policies"],
		errors: {
			classifyStorage(error) {
				if (isIdbQuotaError(error)) return "quota";
				if (isIdbInternalError(error)) return "internal";
				return "other";
			},
			classifyCompletion(error) {
				return classifyAdminCompletionError(error);
			},
			normalize: normalizeAdminCompletionError,
			createMultipartTargetValidationError: (failure, file) => createAdminTargetValidationError(failure, file as File),
			createChunkSourceError: (kind, fileName) =>
				kind === "unreadable"
					? new FileUnreadableError(fileName)
					: kind === "missing"
						? new Error(`Chunk source missing: persisted chunk sizes do not match readable bytes for "${fileName}"`)
						: new Error(`Chunk source ${kind} for "${fileName}"`)
		} satisfies AdminDriverPorts["errors"],
		telemetry: adminUploadTelemetry,
		connectivity: adminConnectivity,
		isPresignedUrlExpired: () => false,
		retryDelay: defaultRetryDelay,
		createId: () => newUploadId(),
		now: () => Date.now(),
		chunkConcurrency: CHUNK_CONCURRENCY,
		maxChunkRetries: MAX_CHUNK_RETRIES,
		maxCloudArrayBufferBytes: MAX_CLOUD_ARRAYBUFFER_BYTES
	} satisfies AdminDriverPorts;
	return ports;
}

/** A fresh scope keeps each resume's durable writer epoch fenced from later retries. */
export function createAdminResumeLeaseScope(sessionId?: string, requestedToken?: unknown): UploadLeaseScope<FileUploadSession, unknown> {
	let expectedToken = requestedToken;
	const scope = createAdminLeaseScope("resume", sessionId, () => expectedToken, false);
	if (!sessionId) return scope;
	const predecessor = latestAdminResumeScopes.get(sessionId);
	let claimedSession: FileUploadSession | undefined;
	let handoffPromise: Promise<void> | undefined;
	const record: AdminResumeScopeRecord = {
		token: scope.token as string,
		predecessor,
		releaseForHandoff(session) {
			return (handoffPromise ??= (async () => {
				const activeSession = claimedSession ?? session;
				try {
					if (claimedSession) {
						const pendingSession = { ...claimedSession };
						await runGuardedEffect(scope.lease, claimedSession, () =>
							adminSessionStore.releasePending(pendingSession, undefined, {
								retryAt: null,
								retryAttempt: pendingSession.retryAttempt ?? 0
							})
						);
					}
				} catch (error) {
					console.warn("[Upload] Could not park superseded resume before handoff", {
						sessionId,
						...uploadFailureLogDetails(error)
					});
				} finally {
					await scope.releaseAndWait(activeSession);
				}
			})());
		}
	};
	latestAdminResumeScopes.set(sessionId, record);
	const release = (session: FileUploadSession) => {
		if (latestAdminResumeScopes.get(sessionId) !== record) {
			void record.releaseForHandoff(session);
			return;
		}
		latestAdminResumeScopes.delete(sessionId);
		scope.release(session);
	};
	return {
		...scope,
		lease: {
			...scope.lease,
			claim(session) {
				let claimed = false;
				const claim = (async () => {
					try {
						if (latestAdminResumeScopes.get(sessionId) !== record) return false;
						let candidate = predecessor;
						while (candidate) {
							const candidateRecord = candidate;
							const candidateClaimed = candidateRecord.claim
								? await candidateRecord.claim.catch((error) => {
										console.warn(
											"[Upload] Predecessor resume claim failed; treating it as unclaimed during handoff",
											{ sessionId, predecessorToken: candidateRecord.token, successorToken: record.token },
											error
										);
										return false;
									})
								: false;
							if (candidateClaimed) {
								expectedToken = candidateRecord.token;
								await candidateRecord.releaseForHandoff(session);
								break;
							}
							candidate = candidateRecord.predecessor;
						}
						if (latestAdminResumeScopes.get(sessionId) !== record) return false;
						claimed = await scope.lease.claim(session);
						if (claimed) claimedSession = session;
						return claimed;
					} finally {
						if (!claimed) release(session);
					}
				})();
				record.claim = claim;
				return claim;
			}
		},
		release
	};
}

/** Legacy single-flight creates this scope eagerly, so it must not enter the persisted-resume handoff chain. */
export function createAdminLegacyResumeLeaseScope(
	sessionId?: string,
	requestedToken?: unknown
): UploadLeaseScope<FileUploadSession, unknown> {
	return createAdminLeaseScope("resume", sessionId, requestedToken, false);
}

/**
 * PUTs a single chunk to a presigned S3 URL, delegating to the canonical progress-aware transport
 * in `helpers/s3.ts` (LSP-16654). That transport drives a STALL timer off `xhr.upload` progress
 * events — reset every time bytes move, fired only after the shared CHUNK_STALL_TIMEOUT_MS (defined
 * in `helpers/s3.ts`) of ZERO progress — replacing the previous fixed 90s wall-clock `fetch` abort
 * (LSP-15716) so a slow-but-progressing upload (below ~56 KB/s) is no longer aborted like a truly
 * stalled one. The single-PUT (FileUploadDialog) and snapshot (api/index.ts) paths now share this
 * exact transport.
 *
 * Contract preserved for `uploadChunk`:
 * - Resolves with a fetch-`Response` (ok/status/statusText, `text()`, `headers.get("ETag")`). The
 *   real ETag response header is surfaced, so the multipart completion path can read the part ETag.
 * - Rejects with a `TypeError` on transport failure (offline/DNS/TCP) — matches `fetch`.
 * - Rejects with a DOMException `AbortError` on stall timeout OR caller cancel — matches the old
 *   `controller.abort()` path so downstream classification (`lastErrorWasNetwork`) is unchanged.
 */
function putChunkWithStallTimeout(
	presignedUrl: string,
	chunkBlob: Blob,
	signal: AbortSignal,
	onProgress?: (bytesUploaded: number) => void,
	headers?: Readonly<Record<string, string>>,
	logContext?: Readonly<Record<string, unknown>>
): Promise<Response> {
	return putToS3WithStallTimeout(chunkBlob, presignedUrl, {
		cancelSignal: signal,
		progressCallback: onProgress ? ({ loaded }) => onProgress(loaded) : undefined,
		headers,
		logContext
	});
}

function createAdminS3ChunkTransport(): S3ChunkTransport {
	return {
		put: async (url, body, { signal, onProgress, headers, logContext }) => {
			const response = await putChunkWithStallTimeout(url, body, signal, onProgress, headers, logContext);
			// Keep PUT-side 404s as response data. The shared uploadChunk classifier owns
			// the typed ambiguous-target refresh/retry path; object-status calls never use
			// this transport and retain their authoritative terminal semantics.
			return projectS3ChunkResponse(response);
		}
	};
}
